import { isEmpty } from 'lodash';
import React from 'react';
import { compose } from 'redux';
import { withProps } from 'recompose';
import { withRouter } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';

import * as ENUM from '@dpdgroupuk/redback-enums';
import { withPageControl } from '@dpdgroupuk/fmx-ui/components/Page';
import { IMAGES } from '@dpdgroupuk/fmx-ui/themes/constants';
import Wizard, { Step } from '@dpdgroupuk/fmx-ui/components/Wizard';
import { withModal } from '@dpdgroupuk/fmx-ui/components/Dialog';

import type { AuthorizedParcel } from '../../../../types';
import {
  ANOTHER_DAY_DELIVERY_OPTION,
  trackable,
} from '../../../../constants/analytics';
import {
  whatHappensNextSpeedDeal,
  doSomethingDifferentSpeedDeal,
  trackAnotherParcelHeader,
  backToDpdSiteHeader,
  backToGreenSiteHeader,
  getDpdAppHeader,
  goBackToDeliveryDetailsSpeedDeal,
  whatHappensNextHeader,
  doSomethingDifferentHeader,
} from '../../../controlActions';
import * as MESSAGES from '../../../../constants/message';
import SelectDayStep from './SelectDayStep';
import CompleteStep from './CompleteStep';
import withParcel from '../../../../HOCs/withParcel';
import { fetchActionsDatesByCode } from '../../actions';
import { RED_STEPS } from '../../steps';
import { withDeliveryUpdate } from '../../hocs';
import { changeDeliveryPreferenceDay } from './actions';
import GetContactDetailsStep from '../../components/GetContactDetailsStep';
import withFetch from '../../../../HOCs/withFetch';
import EnterCodeStep from '../../components/EnterCodeStep';
import GetContactDetailsNoEmailStep from '../../components/GetContactDetailsNoEmailStep';

const useStyles = makeStyles(({ typography, breakpoints }) => ({
  completeStep: {
    paddingLeft: typography.pxToRem(120),
    paddingRight: typography.pxToRem(120),
    [breakpoints.down('xs')]: {
      paddingLeft: typography.pxToRem(15),
      paddingRight: typography.pxToRem(15),
    },
  },
}));

type Props = {
  parcel: AuthorizedParcel,
  dates: Array<string>,
  updateDelivery: Function,
  onSubmitSuccess: Function,
  onSubmitFailure: Function,
};

const ChangeDeliveryDayComponent = ({
  parcel,
  dates,
  updateDelivery,
  onSubmitSuccess,
  onSubmitFailure,
}: Props) => {
  const styles = useStyles();

  return (
    <Wizard
      onSubmit={updateDelivery}
      onSubmitSuccess={onSubmitSuccess}
      onSubmitFailure={onSubmitFailure}
      initialStep={isEmpty(dates) ? RED_STEPS.COMPLETE : RED_STEPS.SELECT_DAY}
    >
      <Step
        stepId={RED_STEPS.SELECT_DAY}
        component={SelectDayStep}
        dates={dates}
        parcel={parcel}
      />

      {/* SMS Authentication */}
      <Step
        stepId={RED_STEPS.GET_CONTACTS}
        component={GetContactDetailsNoEmailStep}
        parcel={parcel}
        nextStep={RED_STEPS.ENTER_CODE}
      />
      <Step
        stepId={RED_STEPS.ENTER_CODE}
        component={EnterCodeStep}
        parcel={parcel}
      />
      {/* / SMS Authentication / */}

      <Step
        stepId={RED_STEPS.GET_CONTACTS_NO_AUTH}
        component={GetContactDetailsStep}
        parcel={parcel}
      />

      <Step
        stepId={RED_STEPS.COMPLETE}
        component={CompleteStep}
        parcel={parcel}
        actionCode={ENUM.PARCEL_ACTION_CODE.RED}
        wrapperClassName={styles.completeStep}
      />
    </Wizard>
  );
};

const createSpeedDealControlActions = props => [
  doSomethingDifferentSpeedDeal(props),
  goBackToDeliveryDetailsSpeedDeal(props),
  whatHappensNextSpeedDeal(props),
];

const createHeaderControlActions = props => [
  doSomethingDifferentHeader(props),
  trackAnotherParcelHeader(props),
  whatHappensNextHeader(props),
  backToDpdSiteHeader(props),
  backToGreenSiteHeader(props),
  getDpdAppHeader(props),
];

const ChangeDeliveryDay = compose(
  withRouter,
  withParcel(),
  withModal,
  withFetch(({ match }) =>
    fetchActionsDatesByCode(match.params.parcelId, ENUM.PARCEL_ACTION_CODE.RED)
  ),
  withProps(props => ({
    dates: props.fetchedData,
    onSubmitSuccess: ({ goToStep }) => goToStep(RED_STEPS.COMPLETE),
  })),
  withDeliveryUpdate({
    actionCode: ENUM.PARCEL_ACTION_CODE.RED,
    changeDeliveryAction: changeDeliveryPreferenceDay,
    trackable: ANOTHER_DAY_DELIVERY_OPTION.ON_SUBMIT,
  }),
  withPageControl({
    title: MESSAGES.CHANGE_DELIVERY_DAY,
    backgroundImage: IMAGES.MAP,
    trackable: trackable(ANOTHER_DAY_DELIVERY_OPTION),
    speedDealActions: createSpeedDealControlActions,
    headerMenuActions: createHeaderControlActions,
  })
)(ChangeDeliveryDayComponent);

export default ChangeDeliveryDay;
